import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import GlobalError from '../components/GlobalError';
import { useSnackbar } from '../contexts/SnackbarProvider';
import axiosInstance from '../utils/axiosInstance';
import { formatDate, formatDateTime, formatCurrency } from '../utils/format';
import { useForm } from 'react-hook-form';
import { calculateTotalAmount, calculateItemSubtotal, calculateSellingPriceIncludingTax } from '../utils/function';
import TableView from '../components/TableView';
import LabelValueItem from '../components/LabelValueItem';
import OrderClientInfo from '../components/OrderClientInfo';
import OrderInfo from '../components/OrderInfo';
import AllocateByWarehouseModal from "../components/AllocateByWarehouseModal";
import useOrderTable from '../hooks/useOrderTable';
import ClientModal from '../components/ClientModal';

import {
  Grid, Box, Typography, Button, Tooltip,
  Stack, Link, Divider, Paper, IconButton, Alert
} from '@mui/material';
import { SupervisorAccount, Delete, Sync, Notifications, Add, AddTask, Edit, Close, LocalShipping, Check, HelpOutline, Link as LinkIcon, LinkOff } from '@mui/icons-material';
import PdfPrintButton from '../components/PdfPrintButton';
import VariantSelectModal from '../components/VariantSelectModal';
const OrderDetailPage = ({ hasLimit }) => {
  const { showSnackbar } = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [selectedClient, setSelectedClient] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [openAllocateModal, setOpenAllocateModal] = useState(false);
  const [openVariantModal, setOpenVariantModal] = useState(false);
  const [selectedOrderItemId, setSelectedOrderItemId] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [isLinkClientModalOpen, setIsLinkClientModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({
    items: []
  });
  const { register, handleSubmit, control, trigger, getValues, formState: { errors }, setValue, reset } = useForm({
    defaultValues: orderData,
  });
  const handleItemDelete = async (index, order_item_id) => {
    const values = getValues();

    // 新規行（バリデーション・confirmなし）
    if (!order_item_id) {
      const updatedItems = values.items.filter((_, i) => i !== index);

      const { total_amount, total_amount_including_tax } =
        calculateTotalAmount(updatedItems);

      setOrderData(prev => ({
        ...prev,
        items: updatedItems,
        total_amount,
        total_amount_including_tax,
      }));

      setValue('items', updatedItems);

      return;
    }

    // バリデーション実行
    const valid = await trigger(); // 全フィールドをチェック
    if (!valid) {
      showSnackbar('入力内容に不備があります。削除できません', 'error');
      return;
    }

    const ok = window.confirm(
      'この明細を削除します。よろしいですか？\n\n' +
      '※ 注文を取り消す場合は「注文キャンセル」ボタンを使用してください。'
    );
    if (!ok) return;

    try {

      // 削除対象を除いた items 配列を作る
      const updatedItems = values.items.filter(item => item.order_item_id !== order_item_id);
      // 合計計算
      const { total_amount, total_amount_including_tax } = calculateTotalAmount(updatedItems);
      // サーバー送信用データ
      const submitData = {
        ...orderData,
        items: updatedItems,
        total_amount,
        total_amount_including_tax,
        delete_item_id: order_item_id,
      };

      // DB 更新
      const res = await axiosInstance.put(`/order/${id}/update`, { orderData: submitData });

      showSnackbar(res?.data?.message || '注文アイテムを削除しました', 'success');

      fetchOrder();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error(err);
      showSnackbar(err?.response?.data?.message || 'この注文は確定済みのため、削除できません', 'error');
    }
  };
  const {
    columns,
    updateItem,
    handleItemChange,
  } = useOrderTable({
    control,
    errors,
    isEditMode: isEditMode && [1, 2, 3].includes(Number(orderData?.order_status_id)),
    hasLimit,
    axiosInstance,
    selectedClient,
    orderData,
    setOrderData,
    calculateSellingPriceIncludingTax,
    calculateItemSubtotal,
    calculateTotalAmount,
    formatCurrency,
    handleItemDelete
  });
  const tableColumns = [
    ...(
      hasLimit('csv_order_upload') && !isEditMode && ['1', '2', '3'].includes(orderData?.order_status_id) ? [
        {
          label: '',
          accessor: 'variant_select',
          width: '60px',
          render: (item) => (
            <Tooltip title="商品紐付け">
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedOrderItemId(item.order_item_id);
                  setOpenVariantModal(true);
                }}
              >
                {
                  item.variant_id
                    ? <LinkIcon fontSize="small" />
                    : <LinkOff fontSize="small" />
                }
              </IconButton>
            </Tooltip>
          ),
        },
      ]
        : []
    ),
    ...columns,
  ];
  const fetchOrder = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/order/${id}`);
      setOrderData(res.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(err);
      }
      setError(err?.response?.data?.message || '取得に失敗しました');
      setOrderData(null);
    }
  }, [id, showSnackbar]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  useEffect(() => {
    if (!orderData) return;

    const formattedItems = (orderData.items || []).map(item => ({
      ...item,
      price: Math.floor(item.price),          // 定価を整数化
      tax_rate: Math.floor(item.tax_rate),    // 税率も整数
    }));

    reset({
      ...orderData,
      items: formattedItems,
      delivery_date: orderData.delivery_date
        ? formatDate(orderData.delivery_date)
        : '',
    });
  }, [orderData, reset]);

  useEffect(() => {
    const snackbarState = location.state?.snackbar;
    if (snackbarState) {
      showSnackbar(snackbarState.message, snackbarState.severity);
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, showSnackbar]);
  const addItem = () => {
    const newItem = {
      product_id: null,
      variant_id: null,
      sku: '',
      price: 0,
      quantity: 1,
      selling_price: 0,
      selling_price_including_tax: 0,
      tax_rate: 0,
      subtotal: 0,
      subtotal_including_tax: 0,
    };

    const newItems = [...orderData.items, newItem];

    setOrderData({
      ...orderData,
      items: newItems,
    });
    setValue(`items.${newItems.length - 1}`, newItem);
  };
  if (!orderData) return null;

  const onSubmit = async (data) => {
    const submitOrderData = {
      ...orderData,
      ...data,
      items: orderData.items,
      total_amount: orderData.total_amount,
      total_amount_including_tax: orderData.total_amount_including_tax
    };
    try {
      const res = await axiosInstance.put(`/order/${id}/update`, { orderData: submitOrderData });
      showSnackbar(res?.data?.message || '注文情報が更新されました', 'success');
      fetchOrder();
      setIsEditMode(false);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(err);
      }
      showSnackbar(err?.response?.data?.message || '更新に失敗しました', 'error');
    }
  };

  const handleApproval = async () => {
    const isValid = await trigger();
    if (!isValid) {
      showSnackbar('入力内容に不備があります', 'error');
      return;
    }
    handleStatusChange({
      status: 3,
      payload: {
        order_number: orderData.order_number,
        order_date: orderData.order_date,
        delivery_date: orderData.delivery_date,
        total_amount: orderData.total_amount,
        client_name: orderData.client_name,
        client_id: orderData.client_id,
        client_contact_name: orderData.client_contact_name,
        client_contact_email: orderData.client_contact_email,
        postal_code: orderData.postal_code,
        state: orderData.state,
        address_line: orderData.address_line,
        payment_method_name: orderData.payment_method_name,
        items: orderData.items,
      },
      successMessage: '注文を承諾メールが送信されました',
    });
  };

  const handleStatusChange = async ({
    status,
    payload = {},
    confirmMessage,
    successMessage,
    navigateTo,
    afterSuccess,
    force = false
  }) => {
    if (confirmMessage && !window.confirm(confirmMessage)) return;

    try {
      const res = await axiosInstance.post('/order/update-status', {
        orderIds: [id],
        status,
        force,
        ...payload,
      });
      // 在庫不足がある場合
      if (res.data.shortages && res.data.shortages.length > 0 && !force) {
        const shortageMessage = res.data.shortages
          .map(s => s.message) // サーバー側で整形済みメッセージを使う
          .join('\n');

        if (window.confirm(`在庫不足があります。このまま承認しますか？\n--\n${shortageMessage}`)) {
          // forceフラグ付きで再実行
          return handleStatusChange({ status, payload, successMessage, navigateTo, afterSuccess, force: true });
        }
        return;
      }
      if (successMessage) {
        showSnackbar(res?.data?.message || '注文情報が更新されました', 'success');
      }
      if (afterSuccess) afterSuccess();
      if (navigateTo) navigate(navigateTo);

    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error(err);
      showSnackbar(err?.response?.data?.message || '更新に失敗しました', 'error');
    } finally {
      fetchOrder();
    }
  };
  const handleLinkClient = async (client) => {
    try {
      await axiosInstance.put(`/order/${id}/link-client`, {
        client_id: client.client_id
      });

      setIsLinkClientModalOpen(false);
      showSnackbar('取引先を紐付けました', 'success');
      fetchOrder();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error(err);
      showSnackbar(err?.response?.data?.message || '更新に失敗しました', 'error');
    }
  };
  if (error) return <GlobalError errorMessage={error} />;
  return (
    <>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Typography variant="h1" component="h1">注文詳細</Typography>
        <div className='button-row'>
          {/* 注文キャンセル */}
          {['1', '2', '3'].includes(orderData.order_status_id) && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={() =>
                handleStatusChange({
                  status: 8, // キャンセルステータス
                  confirmMessage: '本当にキャンセルしますか？キャンセル後は復元できません。',
                  successMessage: '注文をキャンセルしました。',
                  navigateTo: '/orders', // 成功後に移動する場合
                })
              }
            >
              注文のキャンセル
            </Button>
          )}
          {['4', '5', '6'].includes(orderData.order_status_id) && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={() =>
                handleStatusChange({
                  status: 9,
                  confirmMessage: '本当に取り消しますか？取り消し後は復元できません。',
                  successMessage: '注文を取り消しました。',
                  navigateTo: '/orders',
                })
              }
            >
              注文の取り消し
            </Button>
          )}
        </div>
      </Stack>
      <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <Paper variant='section'>
          <Alert severity={orderData.client_id ? 'success' : 'warning'} sx={{ mb: 1 }}>
            {orderData.client_id ? (
              <>
                取引先情報（{orderData.original_client_code || ' -'}：{orderData.original_client_name}）と紐づいています。

              </>
            ) : (
              <>取引先情報と紐づいていません。締め請求の対象になりません。</>
            )}

            {' '}

            <Link
              component="button"
              type="button"
              size="small"
              onClick={() => setIsLinkClientModalOpen(true)}
            >
              （取引先の紐づけ{orderData.client_id ? 'を変更' : 'る'}）
            </Link>
          </Alert>
          {orderData.order_status_id === '4' && (
            <Alert severity="success" sx={{ mb: 2 }}>在庫引当済み</Alert>
          )}
          {orderData.order_status_id === '5' && (
            <Alert severity="success" sx={{ mb: 2 }}>ピッキング中</Alert>
          )}
          {orderData.order_status_id === '6' && (
            <Alert severity="success" sx={{ mb: 2 }}>発送待ち</Alert>
          )}
          {orderData.order_status_id === '7' && (
            <Alert severity="success" sx={{ mb: 2 }}>出荷済み</Alert>
          )}
          {orderData.order_status_id === '10' && (
            <Alert severity="success" sx={{ mb: 2 }}>完了した注文です</Alert>
          )}
          {['8', '9'].includes(orderData.order_status_id) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              取り消し・キャンセルした注文です
            </Alert>
          )}
          {/*orderData.invoice_id &&
            <Alert
              severity="info"
              sx={{ mb: 2 }}
            >
              請求済み
              <Link
                component="button"
                onClick={() => {
                  if (!window.confirm('請求ステータスを未請求に戻しますか？')) return;
                  handleBillingStatusChange(orderData.invoice_id, '未請求');
                }}
                underline="none"
                sx={{ ml: 2 }}
              >
                （未請求に戻す）
              </Link>
            </Alert>
          */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><LabelValueItem label='注文日時' value={formatDateTime(orderData.order_date)} /></Grid>
            <Grid item xs={12} sm={6}><LabelValueItem label='注文番号' value={orderData.order_number} /></Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Typography component='h2' variant="h4" mb={2}>お客様情報</Typography>
            {hasLimit('max_clients') && orderData.client_id && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<SupervisorAccount />}
                onClick={() => navigate(`/clients/${orderData.client_id}`)}
              >顧客情報を確認
              </Button>
            )}
          </Stack>
          <OrderClientInfo
            hasLimit={hasLimit}
            isEditMode={isEditMode}
            register={register}
            control={control}
            setValue={setValue}
            errors={errors}
            fieldErrors={fieldErrors}
            setFieldErrors={setFieldErrors}
          />
          <Divider sx={{ my: 2 }} />
          <OrderInfo
            isEditMode={isEditMode}
            orderData={orderData}
            setOrderData={setOrderData}
            register={register}
            errors={errors}
            control={control}
            setValue={setValue}
          />
          <Divider sx={{ my: 2 }} />
          <Typography component='h2' variant="h4">商品情報</Typography>
          <Alert severity="info">
            各種項目の変更および商品の削除が可能です。商品の追加は新規注文作成を行ってください。
          </Alert>
          {
            orderData.items.some(item => !item.variant_id) && (
              <Alert severity="error" sx={{ mt: 1 }}>
                紐づいていない商品があります。品番から商品を選択するか、編集を破棄した状態で、左のアイコンより商品データとの紐づけを行いましょう。
              </Alert>
            )
          }
          <TableView dataset={orderData.items ?? []} columns={tableColumns} />
          <Button
            type="button"
            variant="outlined"
            startIcon={<Add />}
            onClick={addItem}
            disabled={
              !(
                isEditMode &&
                [1, 2, 3].includes(Number(orderData?.order_status_id))
              )
            }
            size='small'
            sx={{ mt: 1 }}
          >アイテムを追加
          </Button>
          {/* 合計金額 */}
          <Box sx={{ maxWidth: 400, ml: 'auto', mt: 2 }}>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography fontWeight="bold">合計金額（税抜）</Typography>
                <Typography fontWeight="bold">{formatCurrency(orderData.total_amount)}</Typography>
              </Stack>
              <Divider />
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography fontWeight="bold">合計金額（税込）</Typography>
                <Typography fontSize={22} fontWeight="bold">{formatCurrency(orderData.total_amount_including_tax)}</Typography>
              </Stack>
              <Divider />
            </Stack>
          </Box>

          <div className='button-row'>
            {['1', '2', '3', '4', '5', '6'].includes(orderData.order_status_id) && (
              <>
                <Button
                  variant='outlined'
                  startIcon={isEditMode ? <Close /> : <Edit />}
                  onClick={async () => {
                    if (isEditMode) {
                      await fetchOrder();
                    }
                    setIsEditMode(prev => !prev);
                  }}
                >
                  {isEditMode ? '編集を破棄する' : 'この注文を編集'}
                </Button>
                {isEditMode &&
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<Sync />}
                    disabled={!isEditMode}
                  >
                    内容を更新する
                  </Button>
                }
              </>
            )}
            {/* 承諾 */}
            {['1', '2'].includes(orderData.order_status_id) && (
              <Button
                variant="contained"
                startIcon={<Notifications />}
                onClick={handleApproval}
                disabled={isEditMode}
              >
                注文承諾メールを送信
              </Button>
            )}
            {/* 在庫割当を開くボタン */}
            {['3', '4'].includes(orderData.order_status_id) && (
              <Button
                variant={orderData.warehouse_id ? 'outlined' : 'contained'}
                disabled={isEditMode}
                onClick={() => setOpenAllocateModal(true)}
              >
                在庫確認・倉庫割当
              </Button>
            )}

            {/* ピッキング */}
            {hasLimit('max_picking_devices') &&
              orderData.order_status_id === '4' && !isEditMode && (
                <Button
                  variant="contained"
                  startIcon={<AddTask />}
                  disabled={isEditMode}
                  onClick={async () => {
                    try {
                      const res = await axiosInstance.post('/picking/insert', {
                        order_id: id,
                        status: 'READY',
                      });
                      showSnackbar(res?.data?.message || 'ピッキング端末に送信しました。', 'success');
                      fetchOrder();
                    } catch (err) {
                      if (process.env.NODE_ENV === 'development') {
                        console.error(err);
                      }
                      showSnackbar(err?.response?.data?.message || '送信に失敗しました', 'error');
                    }
                  }}
                >
                  ピッキング端末に送信
                </Button>
              )}
            {orderData.order_status_id === '4' && !isEditMode && (
              <Button
                variant="contained"
                onClick={() =>
                  handleStatusChange({
                    status: 6,
                    successMessage: '配送待ちステータスへ変更しました',
                  })
                }
                startIcon={<LocalShipping />}
              >
                発送待ちにする
              </Button>
            )}
            {orderData.order_status_id === '6' && (
              <Button
                variant="contained"
                onClick={() =>
                  handleStatusChange({
                    status: 7,
                    successMessage: '出荷済みステータスへ変更しました',
                  })
                }
                startIcon={<Check />}
              >
                出荷済み
              </Button>
            )}
            {orderData.order_status_id === '7' && (
              <Button
                variant="contained"
                onClick={() =>
                  handleStatusChange({
                    status: 10,
                    successMessage: '完了ステータスへ変更しました',
                  })
                }
                startIcon={<Check />}
              >
                完了にする
              </Button>
            )}
          </div>
          <Divider sx={{ my: 4 }} />
          {hasLimit('invoices_and_delivery') && ['3', '4', '5', '6', '7'].includes(orderData.order_status_id) && (
            <Alert severity="info" icon={<HelpOutline />}>
              帳票に記載される自社情報は「会社設定」で設定できます。
              <Link
                component="button"
                onClick={() => navigate('/setting/invoice')}
                underline="none"
                sx={{ ml: 2 }}
              >
                （会社設定）
              </Link>
            </Alert>
          )}
          <div className='button-row'>
            {orderData.estimate_id &&
              <Button
                variant="outlined"
                size='small'
                onClick={() =>
                  navigate(`/estimates/${orderData.estimate_id}`)
                }
              >
                見積を確認する
              </Button>
            }
            {/* 納品書 */}
            {hasLimit('invoices_and_delivery') && ['3', '4', '5', '6', '7'].includes(orderData.order_status_id) && (
              <>
                <PdfPrintButton
                  id={orderData.order_id}
                  isEditMode={isEditMode}
                  pdfType="shipping"
                  label="出荷指示書"
                  onAfterDownload={fetchOrder}
                />
                <PdfPrintButton
                  id={orderData.order_id}
                  isEditMode={isEditMode}
                  pdfType="deliverynote"
                  label="納品書"
                  onAfterDownload={fetchOrder}
                />
              </>
            )}
          </div>
        </Paper >
      </form>
      <VariantSelectModal
        open={openVariantModal}
        onClose={() => {
          setOpenVariantModal(false);
          setSelectedOrderItemId(null);
        }}
        orderItemId={selectedOrderItemId}
        hasLimit={hasLimit}
        axiosInstance={axiosInstance}
        selectedClient={selectedClient}
        orderData={orderData}
        showSnackbar={showSnackbar}
        onSuccess={() => {
          fetchOrder();
        }}
      />
      <AllocateByWarehouseModal
        open={openAllocateModal}
        orderId={id}
        warehouseId={orderData.warehouse_id}
        onClose={() => setOpenAllocateModal(false)}
        refetch={fetchOrder}
      />
      <ClientModal
        isOpen={isLinkClientModalOpen}
        onClose={() => setIsLinkClientModalOpen(false)}
        onSelectClient={handleLinkClient}
      />
    </>
  );
};

export default OrderDetailPage;
